import { PlusOutlined } from "@ant-design/icons";
import { Button, Form, FormProps, Input, Select } from "antd";
import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import CustomForm from "../../../components/common/CustomForm";
import Loader from "../../../components/common/Loader";
import {
  ArrayOption,
  pathLink,
  selectSearchOption,
} from "../../../helpers/siteConfig";
import AdminLayout from "../../../layouts/AdminLayout";
import { getAllRegionAsync } from "../../../store/features/NP/geoInfo/geoAPI";
import {
  createSupplierAsync,
  getSingleSupplierAsync,
  updateSupplierAsync,
} from "../../../store/features/NP/supplier/supplierAPI";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { SupplierData } from "../../../types/redux/NP/supplier";

export default function ManageSupplier() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { id } = useParams();
  const { postLoading, updateLoading } = useAppSelector(
    (state) => state.network
  );
  const { singleSupplier, viewLoading } = useAppSelector(
    (state) => state.supplier
  );
  const { regions } = useAppSelector((state) => state.npGeo);
  const location = useLocation();
  const path = pathLink(location.pathname, 1);
  const navLink = `/${path}/supplier`;

  const onFinish: FormProps<SupplierData>["onFinish"] = (values) => {
    if (id) {
      dispatch(
        updateSupplierAsync({
          data: values,
          others: {
            reRender,
            id,
          },
        })
      );
    } else {
      dispatch(
        createSupplierAsync({
          data: values,
          others: {
            reRender,
          },
        })
      );
    }
  };

  // redirect and re-render function
  const reRender = (val: boolean) => {
    if (val) navigate(navLink);
  };

  useEffect(() => {
    if (id) {
      dispatch(
        getSingleSupplierAsync({
          params: { id },
        })
      );
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && singleSupplier && updateLoading === false) {
      form.setFieldsValue({
        supplierName: singleSupplier?.supplierName,
        supplierAddress: singleSupplier?.supplierAddress,
        region: singleSupplier?.region?._id,
        walletNumber: singleSupplier?.walletNumber,
        contactNumber: singleSupplier?.contactNumber || [],
      });
    }
  }, [form, id, singleSupplier, updateLoading]);

  useEffect(() => {
    dispatch(getAllRegionAsync({}));
  }, [dispatch]);

  return (
    <AdminLayout title="Add New Supplier" headerTitle="Supplier">
      {viewLoading ? (
        <Loader />
      ) : (
        <CustomForm<SupplierData>
          onFinish={onFinish}
          title={`${id ? "Update" : "Add New"} Supplier`}
          formTitle={`${id ? "Update" : "Add new"} supplier information`}
          backLink={navLink}
          disabled={postLoading}
          formProps={{
            form,
          }}
          isUpdate={!!id}
        >
          <Form.Item
            name="supplierName"
            label="Supplier Name"
            rules={[
              {
                required: true,
                message: "Please enter supplier name",
              },
            ]}
          >
            <Input size="large" placeholder="Enter supplier name" />
          </Form.Item>
          <Form.Item
            name="region"
            label="Select Cluster"
            rules={[
              {
                required: true,
                message: "Please select cluster",
              },
            ]}
          >
            <Select
              placeholder="Select cluster"
              size="large"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={ArrayOption(
                [...regions].sort((a, b) =>
                  a.regionName.localeCompare(b.regionName)
                ),
                "regionName",
                "_id"
              )}
            />
          </Form.Item>
          <Form.Item
            name="supplierAddress"
            label="Supplier Address"
            rules={[
              {
                required: true,
                message: "Please enter supplier address",
              },
            ]}
          >
            <Input size="large" placeholder="Enter supplier address" />
          </Form.Item>

          <Form.Item
            name="walletNumber"
            label="Wallet Number"
            rules={[
              {
                required: true,
                message: "Please enter mobile no",
              },
              {
                pattern: /^(\+88)?(01[3-9])[0-9]{8}$/,
                message: "Please enter a valid Bangladeshi phone number",
              },
              {
                validator: (_, value, callback) => {
                  const contactNumbers =
                    form.getFieldValue("contactNumber") || [];
                  const duplicates = contactNumbers.filter(
                    (item: any) => item === value
                  );
                  if (duplicates.length > 1) {
                    return Promise.reject("Already exists");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input size="large" placeholder="Enter wallet number" />
          </Form.Item>

          {/* <Form.List initialValue={[undefined]} name="contactNumber">
            {(fields, { add, remove }) => {
              return (
                <div>
                  {fields.map((field, index) => (
                    <div
                      className="grid grid-cols-7 items-center"
                      key={field.key}
                    >
                      <Form.Item
                        name={[index]}
                        label="Supplier Contact no"
                        rules={[
                          {
                            required: true,
                            message: "Please enter mobile no",
                          },
                          {
                            pattern: /^(\+88)?(01[3-9])[0-9]{8}$/,
                            message:
                              "Please enter a valid Bangladeshi phone number",
                          },
                        ]}
                        className="col-span-6"
                      >
                        <Input
                          size="large"
                          prefix={"BD "}
                          placeholder="Mobile No:"
                        />
                      </Form.Item>

                      {fields.length > 1 ? (
                        <div className="flex justify-end mt-1">
                          <button
                            className="w-8 h-8 rounded-full bg-[#FFDCDA] border-none cursor-pointer"
                            onClick={() => remove(field.name)}
                          >
                            <DeleteIcon />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                  <div className="flex justify-end mb-3">
                    <Button onClick={() => add()}>
                      <PlusOutlined /> Add Contact
                    </Button>
                  </div>
                </div>
              );
            }}
          </Form.List> */}

          <Form.List initialValue={[undefined]} name="contactNumber">
            {(fields, { add, remove }) => {
              return (
                <div>
                  {fields.map((field, index) => (
                    <div
                      className="grid grid-cols-7 items-center"
                      key={field.key}
                    >
                      <Form.Item
                        name={[index]}
                        label="Supplier Contact no"
                        dependencies={["contactNumber"]}
                        rules={[
                          {
                            required: true,
                            message: "Please enter mobile no",
                          },
                          {
                            pattern: /^(\+88)?(01[3-9])[0-9]{8}$/,
                            message:
                              "Please enter a valid Bangladeshi phone number",
                          },
                          {
                            validator: (_, value, callback) => {
                              const contactNumbers =
                                form.getFieldValue("contactNumber") || [];
                              const duplicates = contactNumbers.filter(
                                (item: any) => item === value
                              );
                              if (duplicates.length > 1) {
                                return Promise.reject("Already exists");
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                        className="col-span-6"
                      >
                        <Input
                          size="large"
                          prefix={"BD "}
                          placeholder="Mobile No:"
                        />
                      </Form.Item>

                      {fields.length > 1 ? (
                        <div className="flex justify-end mt-1">
                          <button
                            type="button"
                            className="w-8 h-8 rounded-full bg-[#FFDCDA] border-none cursor-pointer"
                            onClick={() => remove(field.name)}
                          >
                            <DeleteIcon />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                  <div className="flex justify-end mb-3">
                    <Button type="dashed" onClick={() => add()}>
                      <PlusOutlined /> Add Contact
                    </Button>
                  </div>
                </div>
              );
            }}
          </Form.List>
        </CustomForm>
      )}
    </AdminLayout>
  );
}
